import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Tooltip,
  Card,
  CardContent,
  CardActions,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";

const BroadcastScreen = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Edit State
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Delete State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const q = query(
      collection(db, "web_chat_list", "global_broadcast", "messages"),
      orderBy("timestamp", "desc"),
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setMessages(msgs);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const openEditDialog = (msg) => {
    setSelectedMessage(msg);
    setEditContent(msg.message || "");
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!editContent.trim() || !selectedMessage) return;
    setIsSaving(true);
    try {
      const msgRef = doc(
        db,
        "web_chat_list",
        "global_broadcast",
        "messages",
        selectedMessage.id,
      );
      await updateDoc(msgRef, {
        message: editContent,
        is_edited: true,
        edited_at: serverTimestamp(),
      });

      if (messages.length > 0 && messages[0].id === selectedMessage.id) {
        const globalRef = doc(db, "web_chat_list", "global_broadcast");
        await updateDoc(globalRef, {
          last_message: editContent,
        });
      }

      setEditDialogOpen(false);
    } catch (error) {
      console.error("Error updating broadcast:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const openDeleteDialog = (msg) => {
    setSelectedMessage(msg);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedMessage) return;
    setIsDeleting(true);
    try {
      const msgRef = doc(
        db,
        "web_chat_list",
        "global_broadcast",
        "messages",
        selectedMessage.id,
      );
      await deleteDoc(msgRef);
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error("Error deleting broadcast:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return "Sending...";
    const date = timestamp.toDate();
    return date.toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Tab") {
      e.preventDefault();
      const start = e.target.selectionStart;
      const end = e.target.selectionEnd;
      const spaces = "    ";
      const newValue =
        editContent.substring(0, start) + spaces + editContent.substring(end);
      setEditContent(newValue);
      setTimeout(() => {
        if (e.target) {
          e.target.selectionStart = e.target.selectionEnd =
            start + spaces.length;
        }
      }, 0);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress sx={{ color: "#009fd9" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{ maxWidth: "1200px", margin: "0 auto", pb: 4, px: { xs: 2, sm: 3 } }}
    >
      <Typography
        variant="h4"
        fontWeight="bold"
        sx={{
          color: "#009fd9",
          mb: 4,
          fontSize: { xs: "1.75rem", md: "2.125rem" },
        }}
      >
        Broadcast Messages History
      </Typography>

      {isMobile ? (
        // Mobile Layout: Cards
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {messages.length === 0 ? (
            <Typography
              variant="body1"
              color="textSecondary"
              align="center"
              sx={{ py: 4 }}
            >
              No broadcast messages have been sent yet.
            </Typography>
          ) : (
            messages.map((msg) => (
              <Card
                key={msg.id}
                sx={{
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  border: "1px solid #eee",
                }}
              >
                <CardContent>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                    gutterBottom
                  >
                    {formatDateTime(msg.timestamp)}
                    {msg.is_edited && (
                      <span style={{ fontStyle: "italic", marginLeft: "4px" }}>
                        (Edited)
                      </span>
                    )}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      mt: 1,
                    }}
                  >
                    {msg.message}
                  </Typography>
                </CardContent>
                <CardActions
                  sx={{ justifyContent: "flex-end", pt: 0, px: 2, pb: 2 }}
                >
                  <IconButton
                    onClick={() => openEditDialog(msg)}
                    color="primary"
                    size="small"
                    sx={{ mr: 1 }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => openDeleteDialog(msg)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            ))
          )}
        </Box>
      ) : (
        // Desktop Layout: Table
        <TableContainer
          component={Paper}
          sx={{ boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}
        >
          <Table>
            <TableHead sx={{ backgroundColor: "#f5fcfd" }}>
              <TableRow>
                <TableCell
                  sx={{ fontWeight: "bold", color: "#005b7d", width: "20%" }}
                >
                  Date Sent
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "#005b7d" }}>
                  Message Content
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", color: "#005b7d", width: "120px" }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      No broadcast messages have been sent yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                messages.map((msg) => (
                  <TableRow key={msg.id} hover>
                    <TableCell
                      sx={{ whiteSpace: "nowrap", verticalAlign: "top" }}
                    >
                      {formatDateTime(msg.timestamp)}
                      {msg.is_edited && (
                        <Typography
                          variant="caption"
                          display="block"
                          color="textSecondary"
                          sx={{ fontStyle: "italic" }}
                        >
                          (Edited)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell
                      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {msg.message}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: "top" }}>
                      <Tooltip title="Edit Broadcast">
                        <IconButton
                          onClick={() => openEditDialog(msg)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Broadcast">
                        <IconButton
                          onClick={() => openDeleteDialog(msg)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => !isSaving && setEditDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: "bold", color: "#005b7d" }}>
          Edit Broadcast Message
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Message Content"
            type="text"
            fullWidth
            multiline
            minRows={6}
            maxRows={20}
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSaving}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            sx={{ backgroundColor: "#009fd9" }}
            disabled={!editContent.trim() || isSaving}
          >
            {isSaving ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isDeleting && setDeleteDialogOpen(false)}
      >
        <DialogTitle sx={{ fontWeight: "bold", color: "#d32f2f" }}>
          Delete Broadcast?
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this broadcast message? It will be
            removed from all users' inboxes.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={isDeleting}
          >
            {isDeleting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BroadcastScreen;
